// Jungle Ruins — stage layout (src/world/stages/jungle/). The stage owns every file in this folder:
//   layout.js    level geometry (this file)        props.js    prop pack + placements (set dressing)
//   surfaces.js  stage surface materials (texlib)  murals.js   stage decals / signage (mural atlas)
import { PATTERN, B, R, O, OCT } from '../../mapkit.js';
import { SURF } from './surfaces.js';
import { MURAL_IDS } from './murals.js';

// ------------------------------------------------------------------------------------------------------------
// Jungle Ruins ("Ruins") — an overgrown Mesoamerican temple city in a rainforest basin. Played vertically, the
// opposite of the flat-yard-and-trench stages: the fight climbs a stepped pyramid in the middle, ducks under a
// treetop boardwalk on one flank and drops into a dry cenote sinkhole on the other.
//   • spawn: Sanctuary Terrace (y = 2.8), set off-centre (x = +4 for Alpha, -4 for Bravo) under the temple facade,
//     with a processional stair to the plaza, a west stair to the canopy side and an east stair to the cenote rim
//   • centre: the Great Ziggurat, three hoppable tiers (y = 0.9 → 1.8 → 2.7) round the Sun Monolith; stairs climb
//     it from the spawn side (tier 1), the flanks (tier 2) and the spawn side again (the summit)
//   • canopy flank (-X for Alpha, +X for Bravo): a timber boardwalk in the banyan crowns (y = 2.6) from the rear
//     treehouse deck over a grate walk to the Canopy Lookout over mid; the plaza runs on underneath it
//   • cenote flank (+X for Alpha, -X for Bravo): a 1.2 m deep dry sinkhole with a fallen-log bridge across it and
//     stairs out on three sides
//   • between spawn and mid: Shrine Plaza (the carved shrine, the guardian idol) and a stone gateway framing the
//     pyramid stair
// ------------------------------------------------------------------------------------------------------------
const JR = {
  plaza: '#9ca894',         // carved limestone & basalt plaza flagstones with mossy joints
  floor: '#7d8f78',         // damp mossy forest-floor flagstones round the pyramid
  sanctuary: '#b8b29b',     // warm sun-lit carved limestone sanctuary terrace
  cenote: '#4e6a55',        // the sinkhole's wet mossy floor (darker than the plaza so the drop reads)
  tier1: '#889880',         // ziggurat tiers, darkest at the foot, sun-bleached at the summit
  tier2: '#9aa68e',
  tier3: '#c2ba9f',
  mound: '#788872',         // banyan root mounds
  stoneHi: '#a8a48e',       // carved stone cover blocks, gateway & shrine walls
  cliff: '#5c6e58',         // outer perimeter mossy temple retaining wall (off-limits)
  wood: '#8c6548',          // weathered tropical hardwood boardwalk & log bridge
  woodDk: '#6a4a34',        // boardwalk posts
  step: '#aba892',          // chiselled temple stone steps
  bronze: '#8f7d52',        // oxidised bronze grating
};

const NOPAINT = [[1, 0, 0], [-1, 0, 0], [0, 0, 1], [0, 0, -1], [0, 1, 0]];

const templestone = (c = JR.plaza, o = {}) => ({ color: c, pattern: SURF.templestone, ...o });
const mossystone = (c = JR.floor, o = {}) => ({ color: c, pattern: SURF.mossystone, ...o });
const junglewood = (c = JR.wood, o = {}) => ({ color: c, pattern: SURF.junglewood, ...o });
const cliff = (c = JR.cliff, o = {}) => ({ color: c, pattern: SURF.mossystone, noPaint: NOPAINT, roof: true, ...o });
const stair = (o = {}) => ({ color: JR.step, pattern: PATTERN.stonestep, ...o });
const boards = (o = {}) => ({ color: JR.wood, pattern: PATTERN.rampboard, ...o });
const post = (x, z, y1 = 2.3) => B(x - 0.25, x + 0.25, 0.0, y1, z - 0.25, z + 0.25, junglewood(JR.woodDk));

export const LAYOUT = {
  id: 'jungle',
  biome: 'jungle',
  envBoats: false,
  bounds: { minX: -28, maxX: 28, minZ: -46, maxZ: 46 },
  spawnPads: [[4, 2.8, -41.8], [-4, 2.8, 41.8]],
  spawnBarrier: 4.2,
  // match intro: opens on the Sun Monolith atop the Great Ziggurat, then sweeps back to the Sanctuary Terrace
  intro: { from: [14, 12.5, 11], lookFrom: [0, 4.0, 0], toBack: 3.4 },
  // stage-select picture: 3/4 aerial from over the cenote corner looking across the ziggurat to the canopy walk
  art: { from: [30, 16.0, -34], look: [-3.0, 2.0, 3.0], fov: 60 },

  // ==========================================================================================================
  // SINGLE — the forest floor round the Great Ziggurat, the banyan root mounds and the ruin walls (rotationally
  // paired by hand: every piece at (x, z) has its twin at (-x, -z))
  // ==========================================================================================================
  single: [
    // Forest-floor flagstones (y = 0) across the middle of the basin
    B(-26.0, 26.0, -1.2, 0.0, -10.0, 10.0, mossystone(JR.floor, { tag: 'forest-floor' })),

    // The Great Ziggurat: three tiers, each low enough to hop (0.9 m), stairs on alternating faces
    B(-8.0, 8.0, 0.0, 0.9, -8.0, 8.0, mossystone(JR.tier1, { tag: 'ziggurat-1' })),
    B(-5.5, 5.5, 0.9, 1.8, -5.5, 5.5, templestone(JR.tier2, { tag: 'ziggurat-2' })),
    B(-2.8, 2.8, 1.8, 2.7, -2.8, 2.8, templestone(JR.tier3, {
      tag: 'ziggurat-summit',
      mural: [{ n: [0, 1, 0], id: MURAL_IDS.sunAltar }],
    })),
    // tier 1 → 2 from the flanks (±X)
    R([8.0, 0.9, 0], [5.5, 1.8, 0], 3.0, stair()),
    R([-8.0, 0.9, 0], [-5.5, 1.8, 0], 3.0, stair()),
    // tier 2 → summit from the spawn sides (±Z), offset so the climb zig-zags
    R([-2.6, 1.8, -5.5], [-2.6, 2.7, -2.8], 2.4, stair()),
    R([2.6, 1.8, 5.5], [2.6, 2.7, 2.8], 2.4, stair()),

    // Banyan root mounds on the flanks of the pyramid (low octagons, a tree on each)
    ...OCT(18.0, 2.4, 3.3, 0.0, 0.7, mossystone(JR.mound)),
    ...OCT(-18.0, -2.4, 3.3, 0.0, 0.7, mossystone(JR.mound)),
    // Broken ruin walls between the mounds and the pyramid (waist-high cover, angled across the flank lanes)
    O(12.4, -4.6, 4.2, 0.8, 0.0, 1.5, 28, templestone(JR.stoneHi)),
    O(-12.4, 4.6, 4.2, 0.8, 0.0, 1.5, 28, templestone(JR.stoneHi)),
    O(21.6, -6.2, 2.6, 0.8, 0.0, 1.1, -12, templestone(JR.stoneHi)),
    O(-21.6, 6.2, 2.6, 0.8, 0.0, 1.1, -12, templestone(JR.stoneHi)),

    // Outer mossy retaining walls closing the east / west ends of the basin
    B(-28.0, -26.0, -1.2, 5.2, -10.0, 10.0, cliff()),
    B(26.0, 28.0, -1.2, 5.2, -10.0, 10.0, cliff()),
  ],

  // ==========================================================================================================
  // HALF — Alpha side (-Z), mirrored 180° (x → -x, z → -z) for Bravo (+Z)
  // ==========================================================================================================
  half: [
    // ---------------------------------------------------------------- 1. Plaza flagstones (y = 0), cut round the cenote
    B(-26.0, 12.0, -1.2, 0.0, -45.4, -10.0, templestone(JR.plaza, { tag: 'shrine-plaza' })),
    B(12.0, 26.0, -1.2, 0.0, -45.4, -30.0, templestone(JR.plaza)),
    B(12.0, 26.0, -1.2, 0.0, -16.0, -10.0, templestone(JR.plaza)),

    // ---------------------------------------------------------------- 2. Sanctuary Terrace spawn (y = 2.8, off-centre)
    B(-5.0, 13.0, 0.0, 2.8, -45.4, -38.2, templestone(JR.sanctuary, {
      tag: 'spawn-sanctuary',
      mural: [{ n: [0, 1, 0], id: MURAL_IDS.sanctuaryDeck }],
    })),
    // east wing under the facade's cenote basin
    B(13.0, 18.5, 0.0, 2.8, -45.4, -39.8, mossystone(JR.tier3)),
    // stepped-pyramid backwall (scenery / off-limits roof) and the perimeter retaining walls
    B(-5.5, 18.5, 0.0, 5.4, -46.0, -45.4, cliff(JR.sanctuary)),
    B(-28.0, -5.5, -1.2, 5.4, -46.0, -43.2, cliff()),
    B(18.5, 28.0, -1.2, 5.4, -46.0, -43.2, cliff()),
    B(-28.0, -26.0, -1.2, 5.4, -43.2, -10.0, cliff()),
    B(26.0, 28.0, -2.2, 5.6, -43.2, -10.0, cliff()),
    // front parapets, open over the processional stair
    B(-4.8, 1.4, 2.8, 3.65, -38.7, -38.2, mossystone(JR.stoneHi, { noNav: true })),
    B(6.6, 12.8, 2.8, 3.65, -38.7, -38.2, mossystone(JR.stoneHi, { noNav: true })),
    // three ways down: processional stair (mid), west stair (canopy side), east stair off the wing (cenote rim)
    R([4.0, 0.0, -32.6], [4.0, 2.8, -38.2], 5.0, stair()),
    R([-10.6, 0.0, -41.5], [-5.0, 2.8, -41.5], 3.0, stair()),
    R([16.0, 0.0, -34.0], [16.0, 2.8, -39.8], 3.0, stair()),

    // ---------------------------------------------------------------- 3. Shrine Plaza (y = 0)
    // carved shrine (frieze on its mid-facing wall)
    B(-6.0, -1.0, 0.0, 0.34, -24.5, -20.5, mossystone(JR.mound)),
    B(-5.6, -1.4, 0.34, 3.04, -24.0, -21.2, templestone(JR.stoneHi, {
      roof: true,
      mural: [{ n: [0, 0, 1], id: MURAL_IDS.shrineFrieze }],
    })),
    // stepped plinth block on the east of the plaza
    B(1.8, 4.6, 0.0, 1.1, -28.6, -26.4, templestone(JR.sanctuary)),
    // stone gateway framing the ziggurat stair: two piers and a lintel overhead
    B(-4.6, -3.3, 0.0, 3.5, -13.4, -12.2, templestone(JR.stoneHi)),
    B(3.3, 4.6, 0.0, 3.5, -13.4, -12.2, templestone(JR.stoneHi)),
    B(-4.6, 4.6, 3.5, 4.3, -13.4, -12.2, templestone(JR.stoneHi, { roof: true, noNav: true })),
    // the ziggurat's foot stair (ground → tier 1), straight through the gateway
    R([0.0, 0.0, -11.2], [0.0, 0.9, -8.0], 5.0, stair()),

    // ---------------------------------------------------------------- 4. Canopy flank (-X): the treetop boardwalk (y = 2.6)
    // rear treehouse deck (over the plaza; walk underneath)
    B(-26.0, -17.0, 2.3, 2.6, -39.5, -28.5, junglewood(JR.wood, {
      tag: 'canopy-rear',
      mural: [{ n: [0, 1, 0], id: MURAL_IDS.cloisterRoof }],
    })),
    // boardwalk: planks, a bronze grate span (squids drop through), planks
    B(-23.5, -20.5, 2.3, 2.6, -28.5, -26.0, junglewood()),
    B(-23.5, -20.5, 2.38, 2.6, -26.0, -22.5, { color: JR.bronze, pattern: PATTERN.grate, grate: true, tag: 'canopy-grate' }),
    B(-23.5, -20.5, 2.3, 2.6, -22.5, -20.0, junglewood()),
    // Canopy Lookout over mid
    B(-26.0, -16.0, 2.3, 2.6, -20.0, -12.0, junglewood(JR.wood, { tag: 'canopy-lookout' })),
    // posts (cover on the plaza below)
    post(-17.5, -39.0), post(-17.5, -29.0), post(-16.5, -19.5), post(-16.5, -12.5), post(-20.9, -27.6), post(-20.9, -20.4),
    // ways up: from the spawn-side plaza, from the shrine and from the forest floor
    R([-11.2, 0.0, -33.8], [-17.0, 2.6, -33.8], 2.8, boards()),
    R([-10.2, 0.0, -16.0], [-16.0, 2.6, -16.0], 2.8, boards()),
    R([-21.0, 0.0, -6.2], [-21.0, 2.6, -12.0], 3.0, boards()),

    // ---------------------------------------------------------------- 5. Cenote flank (+X): the dry sinkhole (y = -1.2)
    B(12.0, 26.0, -2.2, -1.2, -30.0, -16.0, mossystone(JR.cenote, { tag: 'cenote' })),
    // fallen-log bridge across it at plaza level, on a stone pier
    B(18.2, 19.8, -0.3, 0.0, -30.0, -16.0, junglewood(JR.wood, { tag: 'log-bridge' })),
    B(18.3, 19.7, -1.2, -0.3, -23.6, -22.4, mossystone(JR.stoneHi)),
    // stairs out: west to the plaza, north to the forest floor, south toward spawn
    R([15.6, -1.2, -20.0], [12.0, 0.0, -20.0], 3.0, stair()),
    R([23.5, -1.2, -19.6], [23.5, 0.0, -16.0], 3.4, stair()),
    R([14.6, -1.2, -26.4], [14.6, 0.0, -30.0], 2.8, stair()),
    // fallen blocks in the pit (cover)
    O(22.6, -25.4, 2.2, 1.8, -1.2, -0.2, 15, mossystone(JR.stoneHi)),
    O(15.6, -23.6, 2.0, 1.4, -1.2, -0.4, -20, mossystone(JR.stoneHi)),
  ],

  decor: {
    lamps: [],
    lampPools: [[-2.2, -32.4], [10.2, -32.4], [-6.4, -12.8], [6.4, -12.8]],
    palms: [],
    flags: [[-4.2, 2.8, -44.8], [12.2, 2.8, -44.8]],
  },
};
